import _ from "lodash";
import changesets from "diff-json";

/*
  "Does this open form hold work the user would lose?" — the gate in front of Discard changes?
  (SS05962).

  It used to be a straight `changesets.diff(formData, originalFormData)` over the two whole
  snapshots, which reads every key in the object, not just the form's fields. formData is not a
  clean mirror of the design: the form elements seed their own bookkeeping into it as they mount
  (`Vue.set(formData, fieldName, null)` in FTSFormElement, `${fieldName}Id`/`${fieldName}Model`
  in FTSDisplay), and a sync round-trip can echo back keys the load default never carried. None of
  those are user input, but propagateDown on the server only emits a key when the entry actually
  holds a value for it, so the seeded companions are ADDED keys against the baseline and the raw
  diff reports them as changes. Module (dropDown) fields are the reliable case — `${fieldName}Model`
  is never in a form default — which is why an untouched form with a Module field always asked to
  discard.

  So compare what the user can actually type into instead: the form design's own fields, with
  blank-vs-absent treated as unchanged. A real edit can only land on a design field, so nothing
  the dialog exists to protect is lost, and no amount of internal bookkeeping can fake one.
*/

/* the form is a confirmation, there is nothing to type into it */
const NO_INPUT_ACTIVITIES = ["duplicate", "delete"];

/* decoration, not data */
const NON_DATA_INPUTS = ["description", "image", "empty", "button", "section"];

/* module / user / tag pickers keep the selected id beside the label */
const KEYED_FIELD_TYPES = [7, 8, 19, 20, 21, 24, 27, 28];

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/* a picker with nothing selected is 0, null or absent depending on who wrote it last */
const isBlankKey = (value) => isBlank(value) || value === 0 || value === "0";

const changed = (current, original, blank) => {
  if (blank(current) && blank(original)) return false;
  if (_.isObject(current) || _.isObject(original)) {
    const diff = changesets.diff({ value: current }, { value: original });
    return !!(diff && diff.length);
  }
  return current !== original;
};

/* commons.getAllElements, minus the import: commons pulls the whole store graph behind it and
   this gate only needs to walk rows > items, descending through sections and tabs */
const collectElements = (form, elements) => {
  if (!form || !Array.isArray(form.rows)) return elements;

  form.rows.forEach((row) => {
    if (!row || !Array.isArray(row.items)) return;

    row.items.forEach((item) => {
      if (!item) return;

      if (item.input === "section") {
        if (item.type === "tab" && Array.isArray(item.tabs)) {
          item.tabs.forEach((tab) => tab && collectElements(tab.design, elements));
        } else if (item.design) {
          collectElements(item.design, elements);
        }
      } else if (item.id) {
        elements.push(item);
      }
    });
  });

  return elements;
};

/* fieldName can be a path ("customer.name"), so read it the same way the elements do */
const read = (data, fieldName) =>
  fieldName && fieldName.indexOf(".") > -1 ? _.get(data, fieldName) : (data || {})[fieldName];

/**
 * @param {Object} form design currently on screen (classificationStore.state.form)
 * @param {Object} formData live form data
 * @param {Object} originalFormData snapshot taken when the form loaded
 * @param {String} activityId activity the form belongs to
 * @returns {Boolean} true when a field the user can edit differs from the snapshot
 */
const isFormDirty = ({ form, formData, originalFormData, activityId }) => {
  if (NO_INPUT_ACTIVITIES.indexOf(activityId) > -1) return false;
  if (!_.isObject(formData) || !_.isObject(originalFormData)) return false;

  const elements = collectElements(form, []);
  const fields = elements.filter(
    (x) =>
      x &&
      x.fieldName &&
      NON_DATA_INPUTS.indexOf(x.input) === -1 &&
      x.input !== "display" &&
      !x.display
  );

  return fields.some((field) => {
    const fieldName = field.fieldName;

    if (changed(read(formData, fieldName), read(originalFormData, fieldName), isBlank)) {
      return true;
    }

    if (KEYED_FIELD_TYPES.indexOf(field.type) > -1 || field.input === "dropDown") {
      const keyName = field.key || `${fieldName}Id`;
      if (changed(read(formData, keyName), read(originalFormData, keyName), isBlankKey)) {
        return true;
      }
    }

    return false;
  });
};

export default isFormDirty;
export { isFormDirty };
